import { useEffect, useMemo, useState } from "react";
import { AUTOSUGGEST_DEBOUNCE_TIME } from "~/utils/constants";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { fetchProductSuggestionsForPreview } from "~/features/bulkAddToCart/api/getPreviewProductSuggestions";
import { type SrpProductData } from "~/utils/models";
import debounce from "lodash/debounce";

export const useProductSuggestions = (inputValue: string) => {
  const [suggestions, setSuggestions] = useState<SrpProductData[]>([]);
  const { val: isOpen, setTrue: open, setFalse: close } = useBooleanState(false);
  const [loading, setLoading] = useState(false);

  const fetchData = useMemo(
    () =>
      debounce((val: string, abortSignal: AbortSignal) => {
        setLoading(true);
        fetchProductSuggestionsForPreview(val, abortSignal).then((res) => {
          const data = res.data.filter((item) => item.designation !== undefined);
          setSuggestions(data);
          setLoading(false);
        });
      }, AUTOSUGGEST_DEBOUNCE_TIME),
    [],
  );

  const shouldBeOpen = isOpen && !!inputValue && inputValue.length >= 3;

  useEffect(() => {
    if (!inputValue || inputValue.length < 3) {
      setSuggestions([]);
      return;
    }
    if (isOpen) {
      const controller = new AbortController();
      fetchData(inputValue, controller.signal);
      return () => controller.abort();
    }
  }, [inputValue, isOpen]);

  return { suggestions, loading, isOpen: shouldBeOpen, open, close };
};
